// Condo units calculator (feat/units-calculator, 2026-10-05).
// Restores the units slider calculator removed during the free-only rework
// (original design recovered from git history, commit a72a62e LandingPage.tsx).
//
// Data source: GET /api/public/condo-pricing (backend lane
// feat/calculator-and-condo-overlay) — the SAME pricing_brackets the
// /admin/pricing → Paid Condo → Precios section edits, whitelisted to
// { min_units, max_units, price_per_unit_cents, flat_cents }, Cache-Control 60s.
// No hardcoded rates: skeleton while loading, graceful fallback on error.
//
// Tier binding: brackets DIFFER per condo tier in live data. condo_small is the
// only tier with Jaime's real configured brackets (1–15u $45 fijo; 16–50 $2.75/u;
// 51–150 $2.50/u; 151–300 $2.20/u; 301+ $1.80/u); the other paid tiers currently
// carry a shared generic curve. Per lane contract, the calculator binds to
// condo_small (the first paid condo plan with configured brackets).
import { useEffect, useMemo, useState } from 'react';

type Bracket = { min_units: number; max_units?: number | null; flat_cents?: number | null; price_per_unit_cents?: number | null };
type PricingPlan = { id: string; name: string; pricing_brackets: Bracket[] };
type PricingData = { currency: string; plans: PricingPlan[] };

// Mirrors backend amountFromBrackets (plan-source.js): flat_cents when present,
// else units × price_per_unit_cents (cents).
function bracketPriceAtCents(brackets: Bracket[], u: number): number | null {
  const row = (brackets || []).find(b => u >= Number(b.min_units) && (b.max_units == null || u <= Number(b.max_units)));
  if (!row) return null;
  if (row.flat_cents != null && Number.isFinite(Number(row.flat_cents))) return Number(row.flat_cents);
  if (row.price_per_unit_cents != null && Number.isFinite(Number(row.price_per_unit_cents))) return u * Number(row.price_per_unit_cents);
  return null;
}

// Monotonic clamp — the price must never DECREASE as unit count increases across
// bracket boundaries (boundary plateaus), same as the original calculator.
function condoPriceCents(brackets: Bracket[], units: number): number | null {
  const u = Math.max(1, Math.floor(units));
  const direct = bracketPriceAtCents(brackets, u);
  if (direct == null) return null;
  let max = direct;
  for (const b of (brackets || [])) {
    if (b.max_units == null) break;
    const cap = Number(b.max_units);
    if (!Number.isFinite(cap) || cap >= u) break;
    const boundary = bracketPriceAtCents(brackets, cap);
    if (boundary != null && boundary > max) max = boundary;
  }
  return max;
}

// Human description of the tier the current unit count falls in, generated FROM
// the brackets (null max_units → open-ended, '301+').
function currentTierLine(brackets: Bracket[], units: number): string | null {
  const u = Math.max(1, Math.floor(units));
  const row = (brackets || []).find(b => u >= Number(b.min_units) && (b.max_units == null || u <= Number(b.max_units)));
  if (!row) return null;
  const min = Number(row.min_units);
  const max = row.max_units == null ? null : Number(row.max_units);
  const range = max == null ? `${min}+ unidades` : max === min ? `${min} unidad${min === 1 ? '' : 'es'}` : `${min}–${max} unidades`;
  if (row.flat_cents != null && Number.isFinite(Number(row.flat_cents))) return `${range}: precio fijo $${(Number(row.flat_cents) / 100).toFixed(2)}/mes`;
  if (row.price_per_unit_cents != null && Number.isFinite(Number(row.price_per_unit_cents))) return `${range}: $${(Number(row.price_per_unit_cents) / 100).toFixed(2)} por unidad/mes`;
  return range;
}

export default function CondoUnitsCalculator() {
  const [data, setData] = useState<PricingData | null>(null);
  const [failed, setFailed] = useState(false);
  const [units, setUnits] = useState(10);

  useEffect(() => {
    let alive = true;
    fetch('/api/public/condo-pricing')
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((j: { success: boolean; data: PricingData }) => {
        if (alive && j?.success && Array.isArray(j.data?.plans)) setData(j.data);
        else if (alive) setFailed(true);
      })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, []);

  // First paid condo plan with at least one priced bracket (live data → condo_small).
  const brackets = useMemo<Bracket[] | null>(() => {
    if (!data) return null;
    const plan = data.plans.find(p => Array.isArray(p.pricing_brackets) && p.pricing_brackets.length > 0);
    return plan ? plan.pricing_brackets : null;
  }, [data]);

  const priceCents = useMemo(() => (brackets ? condoPriceCents(brackets, units) : null), [brackets, units]);
  const tier = useMemo(() => (brackets ? currentTierLine(brackets, units) : null), [brackets, units]);

  if (failed) return null; // graceful fallback: hide the calculator if the endpoint is down

  return (
    <div className="mt-14 max-w-2xl mx-auto bg-white rounded-3xl border border-gray-200 p-8">
      <h3 className="text-lg font-semibold tracking-tight mb-1">Calculadora de unidades</h3>
      <p className="text-sm text-gray-500 mb-6">Indica cuántas unidades tiene tu condominio y te mostramos el costo estimado por mes según la tarifa del plan de pago para condominios.</p>
      {brackets ? (
        <>
          <label htmlFor="condo-calc-units" className="text-sm text-gray-600 mb-2 block">Unidades: <span className="font-semibold text-gray-900">{units}</span></label>
          <input
            id="condo-calc-units"
            type="number"
            min={1}
            max={9999}
            value={units}
            onChange={e => setUnits(Math.min(9999, Math.max(1, Math.floor(Number(e.target.value) || 1))))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4 text-center font-semibold"
          />
          <input
            type="range"
            min={1}
            max={500}
            value={Math.min(units, 500)}
            onChange={e => setUnits(Number(e.target.value))}
            className="w-full accent-[#36CCD8] mb-6"
            aria-label="Unidades del condominio"
          />
          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <p className="text-3xl font-semibold tracking-tight">
              ${((priceCents ?? 0) / 100).toFixed(2)}
              <span className="text-sm font-normal text-gray-500"> /mes</span>
            </p>
            <p className="text-xs uppercase tracking-widest text-gray-400">Costo estimado</p>
          </div>
          <p className="text-xs text-gray-500 mt-3">{tier ?? 'Tarifa no disponible'}</p>
          <p className="text-[11px] text-gray-400 mt-2">Estimación informativa; los precios definitivos se anunciarán al lanzar los planes de pago.</p>
        </>
      ) : (
        <div aria-label="Cargando calculadora">
          <div className="inline-block h-10 w-32 rounded bg-gray-200 animate-pulse align-middle mb-4" />
          <div className="h-2 w-full rounded bg-gray-100 mb-3" />
          <div className="h-2 w-2/3 rounded bg-gray-100" />
        </div>
      )}
    </div>
  );
}
